<template>
  <v-container id="logs-dialog">
    <!--//@*/*
        //    *RPD  App v0.91
        //    * Copyright (c) 2022, Blue Cover Six LLC .John Molt All rights reserved.
        //    Code licensed under contract To Reno Police Department / City of Reno, in perpetuity
        //    */
    //-->

    <v-dialog
      v-model="dialog"
      scrollable
      max-width="1200px"
      fullscreen
      persistent
      no-click-animation
    >
      <v-card
        max-height="900px"
        class="backdrop dialog-card"
        id="swal_log_dialog"
      >
        <v-overlay absolute :value="overlay_loading">
          <v-progress-circular
            indeterminate
            :size="80"
            :width="7"
            color="secondary"
          ></v-progress-circular>
        </v-overlay>

        <v-card-text
          class="backdrop pa-0 white--text"
          style="overflow-y: scroll"
        >
          <v-container class>
            <v-stepper
              v-model="currentStep"
              dark
              vertical
              color="secondary"
              non-linear
              elevation="2"
            >
              <v-stepper-header>
                <div class="stepper_header_container">
                  <h2 class="stepper_header_text text-uppercase">Log Editor</h2>
                  <h3 class="stepper_header_text text-uppercase">
                    {{ mode_title }}
                  </h3>
                </div>

                <v-btn
                  color="secondary"
                  class="black--text mx-1"
                  @click="openHelpDialog()"
                  icon
                >
                  <v-icon large>$vuetify.icons.help</v-icon>
                </v-btn>
              </v-stepper-header>
              <v-stepper-items dark>
                <div v-for="(log, index) in logs_data" :key="`Log : ${index}`">
                  <v-stepper-step
                    color="secondary"
                    editable
                    :step="index + 1"
                    class="stepper_step_header"
                  >
                    <h2
                      v-if="
                        LookupDivisionName(log.logEntryID_LogDivisionID)
                          .length == 0 || log.districts.length == 0
                      "
                    >
                      New Log
                    </h2>
                    <h2 v-else>
                      {{ LookupDivisionName(log.logEntryID_LogDivisionID) }} -
                      {{ log.time }} {{ log.districts }}:
                      {{ log.title }}
                    </h2>
                    <small class="pt-1"
                      >Log {{ index + 1 }} of {{ logs_data.length }}</small
                    >
                  </v-stepper-step>

                  <v-stepper-content
                    dark
                    :step="index + 1"
                    color="secondary"
                    class="stepper_content"
                  >
                    <v-form
                      v-model="log.valid"
                      ref="logForm"
                      v-if="currentStep == index + 1"
                    >
                      <h3>Log Details</h3>
                      <v-card
                        color="backdrop lighten-2"
                        elevation="2"
                        class="mt-1 mb-6 pa-3"
                      >
                        <v-row dense>
                          <v-col cols="12" sm="12" md="12">
                            <v-text-field
                              v-model="log.title"
                              label="Incident Title"
                              maxlength="85"
                            ></v-text-field>
                          </v-col>
                        </v-row>

                        <v-row dense>
                          <v-col cols="12" sm="4" md="4">
                            <v-select
                              :items="divisions_list"
                              item-text="displayName"
                              item-value="logDivisionID"
                              v-model="log.logEntryID_LogDivisionID"
                              label="Division*"
                              placeholder="Select a division"
                              maxlength="60"
                            ></v-select>
                          </v-col>
                          <v-col cols="12" sm="4" md="4">
                            <v-select
                              :items="[
                                'CENTRAL',
                                'CITYWIDE',
                                'NORTH',
                                'SOUTH',
                                'SPD',
                                'WCSO',
                                'WCSP',
                              ]"
                              v-model="log.districts"
                              label="Districts"
                              maxlength="60"
                            ></v-select>
                          </v-col>
                          <v-col cols="12" sm="4" md="4">
                            <v-text-field
                              v-model="log.date"
                              type="date"
                              label="Incident Date*"
                              :rules="[field_rules.required]"
                            ></v-text-field>
                          </v-col>
                        </v-row>

                        <v-row dense>
                          <v-col cols="12" sm="4" md="4">
                            <v-text-field
                              v-model="log.time"
                              label="Incident Time*"
                              v-mask="'####'"
                              maxlength="4"
                              :rules="[field_rules.required]"
                            ></v-text-field>
                          </v-col>

                          <v-col cols="12" sm="4" md="4">
                            <v-text-field
                              v-model="log.location"
                              label="Incident Location"
                              maxlength="60"
                            ></v-text-field>
                          </v-col>

                          <v-col cols="12" sm="4" md="4">
                            <v-text-field
                              v-mask="'##-######'"
                              v-model="log.casenum"
                              label="Case Number"
                              maxlength="60"
                            ></v-text-field>
                          </v-col>
                        </v-row>

                        <v-row dense>
                          <v-col cols="12" sm="4" md="4">
                            <v-text-field
                              v-model="log.additionalCaseNumbers"
                              label="Additional Case Numbers"
                              maxlength="180"
                            ></v-text-field>
                          </v-col>

                          <v-col cols="12" sm="4" md="4">
                            <v-text-field
                              v-model="log.sergeant"
                              label="Sergeant"
                              maxlength="60"
                            ></v-text-field>
                          </v-col>
                          <v-col cols="12" sm="4" md="4">
                            <v-text-field
                              v-model="log.lieutenant"
                              label="Lieutenant"
                              maxlength="60"
                            ></v-text-field>
                          </v-col>
                        </v-row>

                        <small
                          >*Please complete the following required fields</small
                        >
                      </v-card>

                      <h3
                        :class="{
                          detail_error: log.description.length > editorLimit,
                        }"
                      >
                        Incident Details
                      </h3>

                      <tiptap
                        v-model="log.description"
                        :characterLimit="editorLimit"
                        :htmlLimit="editorLimit"
                        uniqueID="tiptap1"
                        ref="description_editor"
                      ></tiptap>

                      <!-- Involved parties -->
                      <logs_parties
                        ref="partiesRef"
                        :party_list="log.partiesInvolved"
                        :logEntryID="log.logEntryID"
                      ></logs_parties>

                      <div class="mt-4"></div>
                    </v-form>

                    <!--Stepper buttons-->
                    <v-card
                      class="backdrop lighten-2 pa-1"
                      elevation="4"
                      v-if="currentStep == index + 1"
                    >
                      <!-- <v-tooltip bottom :disabled="$vuetify.breakpoint.xs">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            color="secondary"
                            class="black--text mr-2"
                            @click="stepLeft"
                            icon
                            :disabled="isFirstStep()"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon>$vuetify.icons.arrowLeft</v-icon>
                          </v-btn>
                        </template>
                        <span>Go to the previous log</span>
                      </v-tooltip> -->

                      <!-- <v-tooltip bottom :disabled="$vuetify.breakpoint.xs">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            color="secondary"
                            class="black--text mr-2"
                            @click="stepRight"
                            icon
                            :disabled="isLastStep()"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon>$vuetify.icons.arrowRight</v-icon>
                          </v-btn>
                        </template>
                        <span>Go to the next log</span>
                      </v-tooltip> -->

                      <v-tooltip bottom :disabled="$vuetify.breakpoint.xs">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            color="secondary"
                            class="black--text mx-2"
                            icon
                            :disabled="!log.valid"
                            @click="priorToSaveLog(index)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon size="30">$vuetify.icons.save</v-icon>
                          </v-btn>
                        </template>
                        <span>Save this log</span>
                      </v-tooltip>

                      <v-tooltip bottom :disabled="$vuetify.breakpoint.xs">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            color="secondary"
                            class="black--text mx-2"
                            icon
                            :disabled="!log.valid"
                            @click="priorToDraftLog(index)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon size="30">$vuetify.icons.draft</v-icon>
                          </v-btn>
                        </template>
                        <span>Draft this log</span>
                      </v-tooltip>

                      <v-tooltip bottom :disabled="$vuetify.breakpoint.xs">
                        <template v-slot:activator="{ on, attrs }">
                          <v-btn
                            text
                            color="red"
                            icon
                            class="black--text mx-2"
                            @click="priorToDeleteLog(index)"
                            v-bind="attrs"
                            v-on="on"
                          >
                            <v-icon size="30">$vuetify.icons.del</v-icon>
                          </v-btn>
                        </template>
                        <span>Delete this log</span>
                      </v-tooltip>
                    </v-card>
                    <div class="my-2"></div>
                  </v-stepper-content>
                </div>

                <div v-if="logs_data.length == 0" class="pa-3">
                  <h3>No Logs Found</h3>
                  <!-- <br />
                  <h3>
                    No logs are currently being edited, created, or viewed.
                    Click the `plus` button at the bottom of the screen to add a
                    new log.
                  </h3>
                  <br />
                  <h3>If you have finished, you may close this screen.</h3> -->
                </div>
              </v-stepper-items>
            </v-stepper>
          </v-container>
        </v-card-text>

        <v-card-actions class="backdrop lighten-1 border-card dialog-actions">
          <!-- ARROWS -->
          <v-tooltip top :disabled="$vuetify.breakpoint.xs">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="secondary"
                class="black--text mr-1"
                @click="stepLeft"
                icon
                :disabled="isFirstStep()"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="32">$vuetify.icons.arrowLeft</v-icon>
              </v-btn>
            </template>
            <span>Go to the previous log</span>
          </v-tooltip>

          <small v-if="currentStep != -1"
            >{{ currentStep }}/{{ logs_data.length }}</small
          >

          <v-tooltip top :disabled="$vuetify.breakpoint.xs">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="secondary"
                class="black--text ml-1"
                @click="stepRight"
                icon
                :disabled="isLastStep()"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="32">$vuetify.icons.arrowRight</v-icon>
              </v-btn>
            </template>
            <span>Go to the next log</span>
          </v-tooltip>

          <v-spacer></v-spacer>

          <!--Add Button-->
          <v-tooltip top :disabled="$vuetify.breakpoint.xs">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="secondary"
                class="black--text add-button"
                fab
                small
                v-if="$store.state.EWCanAdd"
                :disabled="logs_data.length > 10 || isEditMode"
                @click="newLog"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="28">$vuetify.icons.plus</v-icon>
              </v-btn>
            </template>
            <span>Add a new log</span>
          </v-tooltip>

          <v-spacer></v-spacer>

          <!-- This menu only appears in mobile -->
          <v-tooltip
            v-if="$vuetify.breakpoint.xs"
            top
            :disabled="$vuetify.breakpoint.xs"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="secondary"
                class="black--text"
                icon
                large
                :disabled="logs_data.length <= 1"
                @click="bottom_sheet = true"
                v-bind="attrs"
                v-on="on"
              >
                <v-icon size="28">$vuetify.icons.threedots</v-icon>
              </v-btn>
            </template>
            <span>All Logs Menu</span>
          </v-tooltip>

          <!-- These buttons only appears in desktop -->
          <div v-else>
            <v-tooltip top :disabled="$vuetify.breakpoint.xs">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="secondary"
                  class="black--text mx-1"
                  icon
                  large
                  :disabled="logs_data.length <= 1"
                  @click="priorToSaveAll"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon size="28">$vuetify.icons.saveall</v-icon>
                </v-btn>
              </template>
              <span>Save all logs</span>
            </v-tooltip>

            <v-tooltip top :disabled="$vuetify.breakpoint.xs">
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  color="secondary"
                  class="black--text mx-1"
                  icon
                  large
                  :disabled="logs_data.length <= 1"
                  @click="priorToDraftAll"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon size="28">$vuetify.icons.draftall</v-icon>
                </v-btn>
              </template>
              <span>Draft all logs</span>
            </v-tooltip>
          </div>

          <v-tooltip top :disabled="$vuetify.breakpoint.xs">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                color="secondary"
                class="black--text"
                icon
                large
                @click="priorToClose"
                v-bind="attrs"
                v-on="on"
                ><v-icon size="32">$vuetify.icons.windowClose</v-icon></v-btn
              >
            </template>
            <span>Close this dialog</span>
          </v-tooltip>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Help dialog -->
    <helpDialog
      v-if="help_dialog"
      helpID="14dbe545-c209-43b2-a981-4eed0a422cac"
      @closeDialog="closeHelpDialog()"
    >
    </helpDialog>

    <!-- Mobile Bottom Menu -->
    <v-bottom-sheet v-model="bottom_sheet" inset v-if="$vuetify.breakpoint.xs">
      <v-sheet class="backdrop lighten-1" height="350px">
        <v-row style="max-width: 100%" class="pt-2">
          <v-spacer></v-spacer>
          <v-btn
            color="secondary"
            class="black--text"
            icon
            large
            @click="closeBottomSheetMenu()"
            ><v-icon size="32">$vuetify.icons.windowClose</v-icon></v-btn
          ></v-row
        >
        <v-list class="backdrop lighten-1 pa-4 pt-8">
          <v-list-item
            @click="priorToSaveAll()"
            class="backdrop lighten-2 mb-1"
            :disabled="logs_data.length <= 1"
          >
            <v-list-item-icon
              ><v-icon color="secondary" size="28"
                >$vuetify.icons.saveall</v-icon
              ></v-list-item-icon
            >
            <v-list-item-content>
              <v-list-item-title>Save all logs</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            @click="priorToDraftAll()"
            class="backdrop lighten-2 mb-1"
            :disabled="logs_data.length <= 1"
          >
            <v-list-item-icon
              ><v-icon color="secondary" size="28"
                >$vuetify.icons.draftall</v-icon
              ></v-list-item-icon
            >
            <v-list-item-content>
              <v-list-item-title>Draft all logs</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-sheet>
    </v-bottom-sheet>
  </v-container>
</template>

<style lang="scss" scoped>
@import "../../scss/site.scss";

.dialog-card {
  box-shadow: rgba(0, 0, 0, 0.19) 0px 10px 20px, rgba(0, 0, 0, 0.23) 0px 6px 6px;
}

.dialog-title {
  margin-bottom: 2px;
  box-shadow: rgba(0, 0, 0, 0.25) 0px 14px 28px,
    rgba(0, 0, 0, 0.22) 0px 10px 10px;
}
// .add-button {
//   left: 47.2%;
// }
.dialog-actions {
  align-self: center;
  width: 100%;
}

.custom-divider {
  height: 2px;
  background-color: black;
}

.border-card {
  margin: 2px;
}

.stepper_content {
  border-left: grey solid 1px;
}

.stepper_step_header {
  color: black !important;
}

.stepper_header_container {
  width: 80%;
}

.stepper_header_text {
  width: 100%;
}

.detail_error {
  color: var(--v-error-base);
}

.stepper-toolbar {
  border-radius: 4px;
  box-shadow: rgba(17, 17, 26, 0.1) 0px 1px 0px,
    rgba(17, 17, 26, 0.1) 0px 8px 24px, rgba(17, 17, 26, 0.1) 0px 16px 48px;
}

@media only screen and (min-width: 1904px) {
  .dialog-actions {
    max-width: 1885px;
  }
}

@media only screen and (min-width: 1264px) and (max-width: 1904px) {
  .dialog-actions {
    max-width: 1285px;
  }
}

@media only screen and (max-width: 600px) {
  .dialog-actions {
    max-width: 100vw;
  }

  // .add-button {
  //   left: 44.5%;
  // }
}
</style>

<script>
/* eslint-disable */
import { bus_common } from "../../mixins/bus_common";
import tiptap from ".././tiptap.vue";
import logs_parties from "./logs_parties.vue";
const helpDialog = () => import("../help_dialog.vue");
export default {
  name: "logs-dialog",
  mixins: [bus_common],
  props: {
    mode: String,
    logDivisionID: Number,
    logEntryIDs: Array,
  },
  components: { tiptap, logs_parties, helpDialog },
  data() {
    return {
      dialog: true,
      bottom_sheet: false,
      overlay_loading: false,
      valid: false,
      field_rules: {
        required: (value) => !!value || "This field is required",
        min: (v) => v.length >= 8 || "Min 8 characters",
        max25: (v) => v.length <= 60 || "Max 60 characters",
        email: (value) => {
          const pattern =
            /^([a-zA-Z0-9_\.\-])+\@(([a-zA-Z0-9\-])+\.)+([a-zA-Z0-9]{2,4})+$/;
          return pattern.test(value) || "Please provide a valid email address";
        },
        min_phone: (v) =>
          v.length >= 14 || "Please enter a complete phone number",
        phone: (value) => {
          const pattern =
            /^\d{3}(-|\s)\d{3}(-|\s)\d{4}$|^\d{10}$|^1\s\d{3}(-|\s)\d{3}(-|\s)\d{4}$|^(1\s?)?\(\d{3}\)(\s|\-)?\d{3}\-\d{4}$/;
          return pattern.test(value) || "Please provide a valid phone number";
        },
        filerequired: (value) => !!value || "A signature image is required",
        incident_date_rules: (value) => {
          var bool = false;
          if (
            this.field_filters.incident_date_start == "" &&
            this.field_filters.incident_date_end == ""
          ) {
            bool = true;
          } else if (
            this.field_filters.incident_date_start != "" &&
            this.field_filters.incident_date_end != ""
          ) {
            bool = true;
          } else {
            bool = false;
          }
          return bool || "A valid date range must be entered using both fields";
        },
        modified_date_rules: (value) => {
          var bool = false;
          if (
            this.field_filters.modified_date_start == "" &&
            this.field_filters.modified_date_end == ""
          ) {
            bool = true;
          } else if (
            this.field_filters.modified_date_start != "" &&
            this.field_filters.modified_date_end != ""
          ) {
            bool = true;
          } else {
            bool = false;
          }
          return bool || "A valid date range must be entered using both fields";
        },
        assigned_date_rules: (value) => {
          var bool = false;
          if (
            this.field_filters.assigned_date_start == "" &&
            this.field_filters.assigned_date_end == ""
          ) {
            bool = true;
          } else if (
            this.field_filters.assigned_date_start != "" &&
            this.field_filters.assigned_date_end != ""
          ) {
            bool = true;
          } else {
            bool = false;
          }
          return bool || "A valid date range must be entered using both fields";
        },
        closed_date_rules: (value) => {
          var bool = false;
          if (
            this.field_filters.closed_date_start == "" &&
            this.field_filters.closed_date_end == ""
          ) {
            bool = true;
          } else if (
            this.field_filters.closed_date_start != "" &&
            this.field_filters.closed_date_end != ""
          ) {
            bool = true;
          } else {
            bool = false;
          }
          return bool || "A valid date range must be entered using both fields";
        },
      },
      logs_data: [],
      currentStep: -1,
      editorLimit: 3999,
      mode_title: "",
      swal_target: "swal_log_dialog",
      s_del_mode: "f",
      help_dialog: false,
    };
  },
  methods: {
    priorToClose() {
      if (this.logs_data.length == 0) {
        this.closeDialog();
      } else {
        let callback = () => {
          this.closeDialog();
        };
        this.swall_diag(
          "Close?",
          "Are you sure you would like to close this dialog? Any unsaved changes or new logs will be lost.",
          "question",
          callback,
          "Yes",
          "No",
          null,
          this.swal_target
        );
      }
    },
    closeDialog() {
      this.dialog = false;
      this.removeListeners();
      setTimeout(() => {
        this.$emit("closeDialog");
      }, 300);
    },

    validateAllLogs() {
      for (let i = 0; i < this.logs_data.length; i++) {
        if (this.validateLog(i) == false) {
          return false;
        }
      }
      return true;
    },

    // Returns an array copy of all logs
    updateAllLogsDelStatus(delStatus) {
      let AllLogs = [];
      for (let i = 0; i < this.logs_data.length; i++) {
        let log = this.updateLogDelStatus(i, delStatus);
        AllLogs.push(log);
      }
      return AllLogs;
    },

    priorToSaveAll() {
      this.closeBottomSheetMenu();
      if (this.validateAllLogs()) {
        let callback = () => {
          let LogEntries = this.updateAllLogsDelStatus("f");
          this.saveAll(LogEntries);
        };
        this.swall_diag(
          "Save All?",
          `Are you sure you would like to save and publish these ${this.logs_data.length} log(s) ?`,
          "question",
          callback,
          "Yes",
          "No",
          null,
          this.swal_target
        );
      }
    },

    async saveAll(logEntries) {
      var form = {
        token: this.$store.state.user.token,
        logEntries: logEntries,
      };

      let saveStatusMessage = "";
      switch (logEntries[0].s_del) {
        case "f":
          saveStatusMessage =
            "All the log entries were successfully saved and published.";
          break;
        case "d":
          saveStatusMessage =
            "All the log entries were successfully saved as drafts.";
          break;
        default:
          break;
      }

      let callback = () => {
        this.closeDialog();
      };

      this.overlay_loading = true;
      await this.$axios
        .post("/api/logs/SaveLogs", form)
        .then((response) => {
          this.swall_it(
            "Saved!",
            saveStatusMessage,
            "success",
            callback,
            null,
            this.swal_target
          );
          this.overlay_loading = false;
        })
        .catch((error) => {
          this.hande_swal_error(error, this.swal_target);
          this.overlay_loading = false;
        });
    },

    priorToDraftAll() {
      this.closeBottomSheetMenu();
      if (this.validateAllLogs()) {
        let callback = () => {
          let LogEntries = this.updateAllLogsDelStatus("d");
          this.saveAll(LogEntries);
        };
        this.swall_diag(
          "Draft All?",
          `Are you sure you would like to save these ${this.logs_data.length} log(s) as drafts for later usage?`,
          "question",
          callback,
          "Yes",
          "No",
          null,
          this.swal_target
        );
      }
    },

    newLog() {
      let log = {
        logEntryID: 0,
        logEntryID_LogDivisionID: 0,
        valid: false,
        title: "",
        districts: "",
        time: "",
        date: "",
        location: "",
        description: "",
        casenum: "",
        additionalCaseNumbers: "",
        sergeant: "",
        lieutenant: "",
        nvrDescription: "",
        webDescription: "",
        web_location: "",
        web_title: "",
        pressDescription: "",
        show_nw_details: false,
        partiesInvolved: [],
        s_del: "f",
        s_user: this.$store.state.user.email,
        s_createduser: this.$store.state.user.email,
        s_createdate: "1900-01-01",
        s_date: "1900-01-01",
      };

      // Auto-select log type if prop is provided
      if (this.logDivisionID != undefined) {
        log.logEntryID_LogDivisionID = this.logDivisionID;
      }
      // If none provided, set to the first element in the list
      else {
        log.logEntryID_LogDivisionID = this.divisions_list[0].logDivisionID;
      }

      this.logs_data.push(log);

      // If only one log exists, set stepper to it
      if (this.logs_data.length == 1) {
        setTimeout(() => {
          this.currentStep = 1;
        });
      }
    },

    help() {},

    stepLeft() {
      this.currentStep = this.currentStep - 1;
    },

    stepRight() {
      this.currentStep = this.currentStep + 1;
    },

    isLastStep() {
      if (
        this.logs_data.length == this.currentStep ||
        this.logs_data.length == 0
      )
        return true;

      return false;
    },

    isFirstStep() {
      if (1 == this.currentStep || this.logs_data.length == 0) return true;

      return false;
    },

    closeBottomSheetMenu() {
      this.bottom_sheet = false;
    },

    toggleNeverwatchDetails(index) {
      this.logs_data[index].show_nw_details =
        !this.logs_data[index].show_nw_details;
    },

    validateLog(index) {
      let sizeLimit = this.editorLimit;
      let log = JSON.parse(JSON.stringify(this.logs_data[index]));
      if (
        log.description.length > sizeLimit ||
        log.nvrDescription.length > sizeLimit ||
        log.webDescription.length > sizeLimit ||
        log.pressDescription.length > sizeLimit
      ) {
        let msg =
          "One or more of your log detail sections exceed the maximum text length allowed. <br/> <br/> Please reduce the content by either removing HTML formatting or shortening the text to meet the length requirements.";
        this.swall_it("Error!", msg, "error", null, null, this.swal_target);
        return false;
      }

      // Check the incident date is valid
      if (this.date_is_valid(log.date) == false) {
        let msg = `Log entry ${
          index + 1
        } has an invalid date range for the incident date. Please correct it and try again.`;
        this.swall_it("Error!", msg, "error", null, null, this.swal_target);
        this.currentStep = index + 1;
        this.$nextTick(() => {
          this.$refs.logForm[0].validate();
        });
        return false;
      }

      // Check the rest of the fields are valid
      // Note: previously this was done using Vuetify form tag log.valid but instead the fields are manually checked
      // In order to support lazy rendering for the stepper which massively improves stability
      // But we sill use Vuetify to display red errors so we need to switch to the step and then validate

      // if (log.valid == false) {
      //   let msg = `Log entry ${
      //     index + 1
      //   } has one or more errors that are preventing saving. Please correct any errors and try again.`;
      //   this.swall_it("Error!", msg, "error", null, null, this.swal_target);
      //   this.currentStep = index + 1;
      //   this.$refs.logForm[index].validate();
      //   return false;
      // }

      // The only required fields at this time are date, divions and time
      // Division is auto-populated
      // Date is checked before
      // So we can double check time and division here
      if (log.division == "" || log.time == "") {
        let msg = `Log entry ${
          index + 1
        } has one or more errors that are preventing saving. Please correct any errors and try again.`;
        this.swall_it("Error!", msg, "error", null, null, this.swal_target);
        this.currentStep = index + 1;
        this.$nextTick(() => {
          this.$refs.logForm[0].validate();
        });
        return false;
      }

      return true;
    },

    involvedPartiesDobValid(log) {
      for (let i = 0; i < log.partiesInvolved.length; i++) {
        const partie = log.partiesInvolved[i];
        if (this.date_is_valid(partie.dob) == false) {
          return false;
        }
      }
      return true;
    },

    // Returns a copy of the log
    updateLogDelStatus(index, newStatus) {
      let log = JSON.parse(JSON.stringify(this.logs_data[index]));
      log.s_del = newStatus;

      // Before performng any checking, we should not save a blank partie involved records
      const fieldsToCheck = [
        "description",
        "firstname",
        "involvement",
        "lastname",
      ];
      //console.log("Before:", log.partiesInvolved);
      log.partiesInvolved = this.removeEmptyFieldElements(
        log.partiesInvolved,
        fieldsToCheck
      );
      //console.log("After:", log.partiesInvolved);

      //If we are deleting, mark the children as well
      if (newStatus == "t") {
        for (let i = 0; i < log.partiesInvolved.length; i++) {
          log.partiesInvolved[i].s_del = newStatus;
        }
      }

      //If we are saving as a draft, we should only mark the visible childre (not deleted) as draft
      else if (newStatus == "d") {
        for (let i = 0; i < log.partiesInvolved.length; i++) {
          if (log.partiesInvolved[i].s_del == "f") {
            log.partiesInvolved[i].s_del = newStatus;
          }
        }
      }

      //If we are saving as a publish, we should only mark drafts as published
      else if (newStatus == "f") {
        for (let i = 0; i < log.partiesInvolved.length; i++) {
          if (log.partiesInvolved[i].s_del == "d") {
            log.partiesInvolved[i].s_del = newStatus;
          }
        }
      }

      // Probably should not be done here, but let's set any invalid partie DOB to 1-1-1900 if invalid
      // RE: so we can allow parties without DOB, an officer might not have that info, just a desc
      for (let i = 0; i < log.partiesInvolved.length; i++) {
        if (this.date_is_valid(log.partiesInvolved[i].dob) == false) {
          log.partiesInvolved[i].dob = "1900-01-01";
        }
      }

      return log;
    },

    removeEmptyFieldElements(objArray, fieldsToCheck) {
      // Remove elements with only empty values in specified fields
      return objArray.filter((obj) =>
        this.hasNonEmptyFieldValues(obj, fieldsToCheck)
      );
    },
    hasNonEmptyFieldValues(obj, fields) {
      for (const field of fields) {
        //console.log(field);
        if (obj.hasOwnProperty(field) && obj[field] !== "") {
          return true; // Found a non-empty field
        }
      }
      return false; // All specified fields are empty strings
    },

    priorToSaveLog(index) {
      if (this.validateLog(index)) {
        let callback = () => {
          let log = this.updateLogDelStatus(index, "f");
          this.SaveLog(index, log);
        };
        this.swall_diag(
          "Save Log?",
          `Are you sure you would like to save and publish this log?`,
          "question",
          callback,
          "Yes",
          "No",
          null,
          this.swal_target
        );
      }
    },

    async SaveLog(index, log) {
      var form = { token: this.$store.state.user.token, logEntry: log };

      let saveStatusMessage = "";
      switch (log.s_del) {
        case "t":
          saveStatusMessage = "The log entry was successfully deleted.";
          break;
        case "f":
          saveStatusMessage =
            "The log entry was successfully saved and published.";
          break;
        case "d":
          saveStatusMessage =
            "The log entry was successfully saved as a draft.";
          break;
        default:
          break;
      }

      let callback = () => {
        // If we remove the last log, we should auto close the dialog
        if (this.logs_data.length == 0) {
          this.closeDialog();
        }
      };

      this.overlay_loading = true;
      await this.$axios
        .post("/api/logs/SaveLog", form)
        .then((response) => {
          this.removeLog(index);
          this.swall_it(
            "Saved!",
            saveStatusMessage,
            "success",
            callback,
            null,
            this.swal_target
          );
          this.overlay_loading = false;
        })
        .catch((error) => {
          this.hande_swal_error(error, this.swal_target);
          this.overlay_loading = false;
        });
    },

    priorToDraftLog(index) {
      if (this.validateLog(index)) {
        let callback = () => {
          let log = this.updateLogDelStatus(index, "d");
          this.SaveLog(index, log);
        };
        this.swall_diag(
          "Draft Log?",
          `Are you sure you would like to save this log as a draft for later use?`,
          "question",
          callback,
          "Yes",
          "No",
          null,
          this.swal_target
        );
      }
    },

    draftLog() {},

    priorToDeleteLog(index) {
      let callback = () => {
        this.deleteLog(index);
      };
      this.swall_diag(
        "Delete Log?",
        `Are you sure you would like to delete this log?`,
        "question",
        callback,
        "Yes",
        "No",
        null,
        this.swal_target
      );
    },
    deleteLog(index) {
      let log = JSON.parse(JSON.stringify(this.logs_data[index]));
      // If the log has not been saved before, just remove it
      if (log.logEntryID == 0) {
        this.removeLog(index);
        if (this.logs_data.length == 0) {
          this.closeDialog();
        }
      } else {
        if (this.validateLog(index)) {
          let logToDelete = this.updateLogDelStatus(index, "t");
          this.SaveLog(index, logToDelete);
        }
      }
    },

    removeLog(index) {
      this.logs_data.splice(index, 1);
      this.currentStep =
        Math.min(this.currentStep, this.logs_data.length - 1) + 1;
      this.$nextTick(() => {
        this.$refs?.description_editor[0]?.refreshEditor();
      });
    },

    async initNewMode() {
      this.mode_title = "New";
      this.newLog();
    },

    async initEditMode() {
      this.mode_title = "Editing";

      var form = {
        token: this.$store.state.user.token,
        logEntryIDs: this.logEntryIDs,
        s_del: this.s_del_mode,
        dateGrouping: false,
      };
      this.overlay_loading = true;
      await this.$axios
        .post("/api/logs/GetByList", form)
        .then((response) => {
          this.logs_data = response.data;
          this.fixLogDataDates();
          this.overlay_loading = false;
        })
        .catch((error) => {
          this.hande_swal_error(error, this.swal_target);
          this.overlay_loading = false;
        });

      if (this.logs_data.length > 0) {
        setTimeout(() => {
          this.currentStep = 1;
        });
      }
      //BUG : First animation of stepper is skipped if init at first item
      //Therefore after view inits, go to first item
    },

    fixLogDataDates() {
      // Any date fields from the server must be conveted to appear ioi nfield
      for (let i = 0; i < this.logs_data.length; i++) {
        this.logs_data[i].date = this.$moment(this.logs_data[i].date).format(
          "YYYY-MM-DD"
        );
        for (let j = 0; j < this.logs_data[i].partiesInvolved.length; j++) {
          this.logs_data[i].partiesInvolved[j].dob = this.$moment(
            this.logs_data[i].partiesInvolved[j].dob
          ).format("YYYY-MM-DD");

          // Any dates with '1900-01-01' should be set to empty
          if (this.logs_data[i].partiesInvolved[j].dob == "1900-01-01") {
            this.logs_data[i].partiesInvolved[j].dob = "";
          }
        }
      }
    },

    LookupDivisionName(divisionID) {
      if (this.divisions_list) {
        for (let index = 0; index < this.divisions_list.length; index++) {
          const element = this.divisions_list[index];
          if (element.logDivisionID == divisionID) {
            return element.displayName;
          }
        }
      }
      return "Division Not Found";
    },

    openHelpDialog() {
      this.help_dialog = true;
    },

    closeHelpDialog() {
      this.help_dialog = false;
    },

    confirmClose(event) {
      event.preventDefault();
      event.returnValue = ""; // For older browsers
      return ""; // For newer browsers
    },

    // handlePopState(event) {
    //   var r = confirm("You pressed a Back button! Are you sure?!");

    //   if (r == true) {
    //     // Call Back button programmatically as per user confirmation.
    //     history.back();
    //     // Uncomment below line to redirect to the previous page instead.
    //     // window.location = document.referrer // Note: IE11 is not supporting this.
    //   } else {
    //     // Stay on the current page.
    //     //history.pushState(null, null, window.location.pathname);
    //   }

    //   history.pushState(null, null, window.location.pathname);
    // },

    addListeners() {
      //window.addEventListener("beforeunload", this.confirmClose);
      //window.addEventListener("popstate", this.handlePopState);
    },

    removeListeners() {
      //window.removeEventListener("beforeunload", this.confirmClose);
      //window.removeEventListener("popstate", this.handlePopState);
    },

    async init() {
      switch (this.mode) {
        case "new":
          this.initNewMode();
          break;
        case "edit":
          this.s_del_mode = "f";
          this.initEditMode();
          break;
        case "draft":
          this.s_del_mode = "d";
          this.initEditMode();
          break;
        default:
          this.initNewMode();
          break;
      }
      this.addListeners();
    },
  },
  watch: {},

  computed: {
    isEditMode() {
      return this.mode_title == "Editing";
    },
    isNewMode() {
      return this.mode_title == "New";
    },
    divisions_list() {
      return this.$store.state.logDivisions;
    },
  },

  mounted() {
    this.init();
  },

  beforeUnmount() {
    this.removeListeners();
  },
};
</script>
